import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { map } from 'rxjs';
import { BrokerService } from '../core/broker/broker.service';
import { RoleGuardService } from '../services/role-guard.service';

/**
 * Denies access when the active broker is not among the brands the signed-in
 * account is assigned to (see the `Chill` / `QuoteDevil` app roles).
 *
 * Nested under MsalGuard in app.routes.ts, so it runs only after MsalGuard has
 * processed the redirect and populated the account cache — the roles claim is
 * therefore available here. If no account exists (e.g. MsalGuard is mid-redirect
 * to AAD on first visit), defer rather than deny: MsalGuard owns the no-auth
 * case, and blocking here would fire a spurious "not assigned" alert.
 */
export const brokerAccessGuard: CanActivateFn = () => {
  const broker = inject(BrokerService);
  const msal = inject(MsalService);

  if (msal.instance.getAllAccounts().length === 0) {
    return true;
  }

  return inject(RoleGuardService)
    .Authorization()
    .pipe(
      map((authorization) => {
        if (authorization.allowedBrokers.includes(broker.brokerId())) {
          return true;
        }
        window.alert(
          'Your account is not assigned to this brand. Please contact your administrator.'
        );
        return false;
      })
    );
};
